import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import ConditionalSupportWidget from './ConditionalSupportWidget';
import { API_URL } from '../config';

interface SupportMessage {
  id: number;
  text: string;
  sender: 'user' | 'support';
  timestamp: Date;
}

interface SupportContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  messages: SupportMessage[];
  addMessage: (text: string, sender: 'user' | 'support') => void;
  sendMessageToBackend: (text: string) => Promise<void>;
  clearMessages: () => void;
  isTyping: boolean;
  setIsTyping: (typing: boolean) => void;
}

const SupportContext = createContext<SupportContextType | undefined>(undefined);

export const SupportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([
    {
      id: 1,
      text: "مرحباً بك! كيف يمكننا مساعدتك اليوم في رحلات عمان، التأشيرات، أو تأجير السيارات؟\n\nHello! How can we help you with Oman tours, visas, or car rentals today?",
      sender: 'support',
      timestamp: new Date()
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const addMessage = (text: string, sender: 'user' | 'support') => {
    const newMessage: SupportMessage = {
      id: Date.now(),
      text,
      sender,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const sendMessageToBackend = async (userText: string) => {
    // 1. Add user message to state
    addMessage(userText, 'user');
    setIsTyping(true);

    try {
      // 2. Call Laravel API
      const response = await axios.post(`${API_URL}/chatbot/message`, {
        message: userText
      });

      if (response.data && response.data.reply) {
        addMessage(response.data.reply, 'support');
      }
    } catch (error) {
      addMessage(
        "Sorry, we are currently experiencing connection issues. Please try again later.",
        'support'
      );
    } finally {
      setIsTyping(false);
    }
  };

  const clearMessages = () => {
    setMessages([
      {
        id: 1,
        text: "Hello! How can we help you with Oman tours, visas, or car rentals today?",
        sender: 'support',
        timestamp: new Date()
      }
    ]);
  };

  return (
    <SupportContext.Provider value={{
      isOpen,
      setIsOpen,
      messages,
      addMessage,
      sendMessageToBackend,
      clearMessages,
      isTyping,
      setIsTyping
    }}>
      {children}
      <ConditionalSupportWidget /> 
    </SupportContext.Provider>
  );
};

export const useSupport = () => {
  const context = useContext(SupportContext);
  if (context === undefined) {
    throw new Error('useSupport must be used within a SupportProvider');
  }
  return context;
};